import * as React from 'react'
import { isElementFocused } from '../app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/utils/focus'
import throttle from 'lodash.throttle'
import debounce from 'lodash.debounce'
import { PartialDeep } from 'type-fest'
import { mergeDeepForUpdate } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'

export const useSyncedValue = <
  T,
  Value extends
    | string
    | undefined
    | Record<string, unknown>
    | Array<unknown> = string,
>({
  value,
  setValue,
  shouldSync,
  enabled,
  rateLimitMs = 1000,
  rateLimitType = 'throttle',
  customEventsKey: _customEventsKey,
}: {
  value: Value
  setValue: (
    newValue: Value,
    opts: {
      syncRef: { current: () => void }
      internalValueRef: { current: Value }
      customEventsKey: string
    }
  ) => void
  shouldSync?: (element: T | null, syncCount: number) => boolean
  enabled?: boolean
  rateLimitMs?: number
  rateLimitType?: 'throttle' | 'debounce' | 'disabled'
  customEventsKey?: string
}) => {
  const elementRef = React.useRef<T>(null)
  const [internalValue, setInternalValue] = React.useState(value)
  const internalValueRef = React.useRef(internalValue)
  // eslint-disable-next-line react-compiler/react-compiler
  internalValueRef.current = internalValue
  const syncCountRef = React.useRef(0)
  const fallbackCustomEventsKey = React.useId()
  const customEventsKey = _customEventsKey ?? fallbackCustomEventsKey

  // the setValue function can change on every render, so we ref it for the debouncer.
  const setValueRef = React.useRef(setValue)
  // eslint-disable-next-line react-compiler/react-compiler
  setValueRef.current = setValue

  const shouldSyncRef = React.useRef(shouldSync)
  // eslint-disable-next-line react-compiler/react-compiler
  shouldSyncRef.current = shouldSync

  const onSync = React.useCallback(() => {
    setInternalValue(value)
    internalValueRef.current = value
    syncCountRef.current += 1
  }, [value])

  const onSyncRef = React.useRef(onSync)
  // eslint-disable-next-line react-compiler/react-compiler
  onSyncRef.current = onSync

  const throttledSetValue = React.useMemo(() => {
    if (rateLimitType === 'throttle') {
      return throttle(
        // eslint-disable-next-line react-compiler/react-compiler
        (newValue: Value) =>
          setValueRef.current(newValue, {
            syncRef: onSyncRef,
            internalValueRef: internalValueRef,
            customEventsKey,
          }),
        rateLimitMs || 0
      )
    } else if (rateLimitType === 'debounce') {
      return debounce(
        // eslint-disable-next-line react-compiler/react-compiler
        (newValue: Value) =>
          setValueRef.current(newValue, {
            syncRef: onSyncRef,
            internalValueRef: internalValueRef,
            customEventsKey,
          }),
        rateLimitMs || 0
      )
    } else {
      return (newValue: Value) =>
        setValueRef.current(newValue, {
          syncRef: onSyncRef,
          internalValueRef: internalValueRef,
          customEventsKey,
        })
    }
  }, [rateLimitType, rateLimitMs, customEventsKey])

  const handleChange = React.useCallback(
    <MergeDeep = false>(
      value:
        | Value
        | (NoInfer<MergeDeep> extends true ? PartialDeep<Value> : Value)
        | ((p: Value) => Value),
      opts?: { mergeDeep?: MergeDeep }
    ) => {
      const newValue = (
        typeof value === 'function'
          ? value(internalValueRef.current)
          : opts?.mergeDeep
            ? mergeDeepForUpdate(internalValueRef.current, value)
            : value
      ) as Value
      setInternalValue(newValue)
      internalValueRef.current = newValue
      React.startTransition(() => {
        throttledSetValue(newValue)
      })
    },
    [throttledSetValue]
  )

  React.useEffect(() => {
    if (!enabled) return
    if (syncCountRef.current > 0) return // already synced once
    onSync()
  }, [enabled, onSync])

  // decide if we should sync
  React.useEffect(() => {
    const shouldSync = shouldSyncRef.current
      ? shouldSyncRef.current(elementRef.current, syncCountRef.current)
      : undefined

    if (shouldSync === false) {
      return // explicitly not syncing
    }
    if (
      shouldSync === true ||
      // no element (free to sync without worrying about focus)
      !elementRef.current ||
      // element exists, but not focused
      (elementRef.current instanceof HTMLElement &&
        isElementFocused(elementRef.current) === false)
    ) {
      onSync()
    }
  }, [onSync])

  React.useEffect(() => {
    return () => {
      syncCountRef.current = 0
    }
  }, [])

  React.useEffect(() => {
    if (!customEventsKey) return

    const handleCustomEvent = (e: Event) => {
      if (e instanceof CustomEvent) {
        if (e.detail?.action === 'sync') {
          onSyncRef.current()
        }
      }
    }

    window.addEventListener(customEventsKey, handleCustomEvent)
    return () => {
      window.removeEventListener(customEventsKey, handleCustomEvent)
    }
  }, [customEventsKey])

  return {
    value: internalValue,
    valueRef: internalValueRef,
    onChange: handleChange,
    ref: elementRef,
    sync: onSync,
  }
}
